import { type ChangeEvent, memo, useState } from 'react';

import { MenuItem, type MenuItemProps } from '@szhsin/react-menu';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';

import Render from 'components/render';
import { WalletEquity } from 'components/WalletEquity';
import { WalletIcons } from 'components/WalletIcons';
import { STORAGE_KEYS } from 'constants/enums';
import routes from 'constants/routes';
import {
    cropAfterDecimals,
    isNan,
    negativeBalanceCheck,
    numberCommaSeparator,
} from 'helpers/utils';
import { precisionForZeroBalance } from 'helpers/wallet';
import subscribe from 'HOC/subscribe';
import useTotalEquity from 'hooks/componentHooks/useTotalEquity';
import useLocalStorage from 'hooks/useLocalStorage';
import useCustomMediaQuery from 'hooks/useMediaQuery';
import i18n from 'i18n/config';
import {
    deltaCashSelector,
    sortedEnabledWalletSelectors,
} from 'selectors/walletSelector';
import { useAppSelector } from 'storeHooks';
import { AngleDown, AngleRightIcon } from 'styles/assets/icons';
import { type IBalance } from 'types/IWallet';
import Box from 'UIKit/Box';
import DeltaButton from 'UIKit/DeltaButton';
import DeltaTable, { Align, Columns } from 'UIKit/DeltaTable';
import { UnderlineTab, UnderlineTabs } from 'UIKit/DeltaUnderlineTabs';
import { FaqItem } from 'UIKit/FaqItem';
import MuiCheckBox from 'UIKit/MuiCheckBox';

import { WALLET_TABS } from './WalletDetails.constants';
import { getBalancesToDisplay } from './WalletDetails.helpers';
import styles from './walletDetails.module.scss';
import {
    DetailsContentProps,
    type TotalTradingEquityProps,
    type WalletDetailTabList,
    type WalletTabsProps,
} from './WalletDetails.types';
// #region constants

const TRADING_FEE_DELTA_CASH_DISCOUNT = '25';

const WALLET_TAB_LIST: WalletDetailTabList = [
  {
    id: WALLET_TABS.TRADING,
    text: i18n.t<string>('headerFooter:header.wallet.trading'),
  },
  {
    id: WALLET_TABS.DELTA_CASH,
    text: i18n.t<string>('headerFooter:header.wallet.deltaCash'),
  },
];

const DELTA_CASH_FAQ = [
  {
    id: 1,
    question: i18n.t<string>('headerFooter:deltaCash.faqQues1'),
    answer: i18n.t<string>('headerFooter:deltaCash.faqAns1', {
      tradingFeeDiscount: TRADING_FEE_DELTA_CASH_DISCOUNT,
    }),
  },
  {
    id: 2,
    question: i18n.t<string>('headerFooter:deltaCash.faqQues2'),
    answer: i18n.t<string>('headerFooter:deltaCash.faqAns2', {
      tradingFeeDiscount: TRADING_FEE_DELTA_CASH_DISCOUNT,
    }),
  },
];

const TRADING_DETAILS_TABLE_COLUMNS: Columns<IBalance>[] = [
  {
    key: 'id',
    title: i18n.t<string>('headerFooter:header.wallet.coin'),
    dataIndex: 'id',
    align: Align.left,
    headerCellClassName: styles.firstCellHeader,
    bodyCellClassName: styles.firstCellData,
    renderView: ({ rowData }) => {
      return (
        <Box flex="inline" as="span" gap={8} alignItems="end">
          <WalletIcons className={styles.walletIcon} coin={rowData.asset.symbol} />
          <Box as="span" textColor="secondary" className={styles.walletSymbolText}>
            {rowData.asset.symbol}
          </Box>
        </Box>
      );
    },
  },
  {
    key: 'id',
    dataIndex: 'id',
    align: Align.right,
    title: i18n.t<string>('headerFooter:header.wallet.value'),
    headerCellClassName: styles.valueHeader,
    bodyCellClassName: styles.valueBody,
    renderView: ({ rowData }) => (
      <WalletEquity coinBalance={rowData} className={styles.walletEquity} />
    ),
  },
  {
    key: 'id',
    dataIndex: 'id',
    align: Align.right,
    title: i18n.t<string>('headerFooter:header.wallet.available'),
    headerCellClassName: styles.availableMarginHeader,
    bodyCellClassName: styles.availableMargin,
    renderView: ({ rowData: { available_balance: availableBalance, asset } }) => {
      const commaSeperatedValue = numberCommaSeparator(
        negativeBalanceCheck(availableBalance, asset?.minimum_precision)
      );
      return precisionForZeroBalance(
        availableBalance,
        commaSeperatedValue,
        asset?.minimum_precision
      );
    },
  },
];

const TRADING_DETAILS_MOBILE_COLUMNS: Columns<IBalance>[] = [
  ...TRADING_DETAILS_TABLE_COLUMNS,
  {
    key: 'id',
    dataIndex: 'id',
    align: Align.right,
    title: '',
    headerCellClassName: styles.arrowHeader,
    bodyCellClassName: styles.arrowBody,
    renderView: ({ rowData }) => {
      return (
        <Box
          className={styles.arrow}
          data-testid={`wallet-trading-asset-${rowData.asset_symbol}`}
        >
          <AngleRightIcon height={9} width={6} color="currentColor" />
        </Box>
      );
    },
  },
];

// #endregion

const MenuItemWrapper = ({ children, ...props }: MenuItemProps) => {
  const { whenBelowMd } = useCustomMediaQuery();

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    (<Render when={whenBelowMd} else={<MenuItem {...props}>{children}</MenuItem>}>
      {children}
    </Render>)
  );
};

const TotalTradingEquity = subscribe(({ className }: TotalTradingEquityProps) => {
  const totalBalanceUSD = useTotalEquity();

  return (
    <Box as="span" className={className}>
      {`$ ${isNan(totalBalanceUSD) ? '-' : numberCommaSeparator(totalBalanceUSD)}`}
    </Box>
  );
});

const WalletMenuTabs = ({ activeTab, onTabChange, tabList }: WalletTabsProps) => (
  <UnderlineTabs
    data-testid="wallet-detail-tabs"
    value={activeTab}
    onChange={(_, val) => onTabChange(val)}
    classes={{
      flexContainer: styles.flexTabsContainer,
      root: styles.walletDetailTabsContainer,
    }}
    TabIndicatorProps={{ className: styles.tabIndicatorClass }}
  >
    {tabList.map(({ id, text, hideTab }) => {
      return hideTab ? null : (
        <UnderlineTab
          classes={{ selected: styles.selectedTab, root: styles.walletDetailTab }}
          value={id}
          key={id}
          data-testid={`wallet-detail-tab-${id}`}
          disableRipple
          focusRipple={false}
          label={text}
        />
      );
    })}
  </UnderlineTabs>
);

const TradingDetails = () => {
  const [hideAssetWithZeroBalance, setAssetWithHideZeroBalance] = useLocalStorage(
    STORAGE_KEYS.HIDE_ASSET_WITH_ZERO_BALANCE,
    false
  );

  const { whenBelowMd } = useCustomMediaQuery();

  const balances = useAppSelector(sortedEnabledWalletSelectors);

  const balanceArray = getBalancesToDisplay(
    whenBelowMd,
    hideAssetWithZeroBalance,
    balances
  );

  const history = useHistory();

  const columns = whenBelowMd
    ? TRADING_DETAILS_MOBILE_COLUMNS
    : TRADING_DETAILS_TABLE_COLUMNS;

  const handleRowClick = (rowData: IBalance) => {
    if (whenBelowMd) {
      history.push(`${routes.balanceCoin}/${rowData.asset.symbol}`);
    }
  };

  const handleToggleHideZeroClick = (event: ChangeEvent<HTMLInputElement>) => {
    setAssetWithHideZeroBalance(event.target.checked);
  };

  return (
    <Box className={styles.tradingDetailsContainer}>
      <Box className={styles.tradingEquity}>
        <Box as="span" className={styles.description} textColor="secondary">
          {i18n.t<string>('headerFooter:header.wallet.walletValue')}
        </Box>
        <TotalTradingEquity className={styles.value} />
      </Box>
      <Box className={styles.toggleZero} flex="inline" alignItems="center" gap={8}>
        <MuiCheckBox
          data-testid="hide-zero-balance"
          checked={hideAssetWithZeroBalance}
          unCheckedClassName={styles.hideZeroCheckBox}
          onChange={handleToggleHideZeroClick}
          classes={{ root: styles.hideZeroCheckBox }}
        />
        <Box as="span" textColor="secondary" fontSize="sm2">
          {i18n.t<string>('headerFooter:header.wallet.hideZeroBalance')}
        </Box>
      </Box>
      <MenuItemWrapper className={styles.tradingDetailsMenu}>
        <DeltaTable
          rowHeight={48}
          tableContainerClass={styles.tradingDetailsTable}
          headerRowClass={styles.headerRow}
          columns={columns}
          data={balanceArray}
          bodyRowClass={styles.bodyRow}
          onRowClick={({ rowData }) => handleRowClick(rowData)}
          autoSizeAllColumns
        />
      </MenuItemWrapper>
      <Render when={!whenBelowMd}>
        <MenuItemWrapper className={styles.viewAllButton}>
          <DeltaButton
            as={Link}
            data-testid="view-all-balance"
            to={routes.account.balances}
            className={styles.viewBtn}
            variant="link-t1"
          >
            <Box as="span">{i18n.t<string>('headerFooter:header.wallet.viewAll')}</Box>
            <AngleRightIcon
              color="var(--brand-bg-primary)"
              height={18}
              width={18}
              className={styles.icon}
            />
          </DeltaButton>
        </MenuItemWrapper>
      </Render>
    </Box>
  );
};

const WalletDetailsFAQ = () => {
  return (
    <Box flex="flex" flexDir="column" textColor="primary" className={styles.FaqContainer}>
      {DELTA_CASH_FAQ.map(({ answer, id, question }) => (
        <FaqItem
          key={id}
          id={id}
          classes={{
            answer: styles.faqAns,
            button: styles.btn,
            question: styles.faqQuestion,
            root: styles.faqRoot,
          }}
          collapseIcon={<AngleRightIcon className={styles.icon} height={18} width={18} />}
          question={question}
          answer={answer}
          expandIcon={
            <AngleDown
              color="var(--brand-bg-primary)"
              height={18}
              width={18}
              className={styles.downIcon}
            />
          }
        />
      ))}
    </Box>
  );
};

const DeltaCashDetails = () => {
  const deltaCash = useAppSelector(deltaCashSelector);

  return (
    <Box className={styles.deltaCashDetails}>
      <Box
        className={styles.balanceContainer}
        flex="flex"
        alignItems="center"
        justifyContent="between"
      >
        <Box className={styles.description} textColor="secondary">
          {i18n.t<string>('headerFooter:header.wallet.deltaCashBalance')}
        </Box>
        <Box
          textColor="primary"
          className={styles.value}
          data-testid="wallet-delta-cash-value"
        >
          {isNan(deltaCash)
            ? '-'
            : `$${numberCommaSeparator(cropAfterDecimals(Number(deltaCash), 2))}`}
        </Box>
      </Box>
      <Box className={styles.dataContent}>
        <WalletDetailsFAQ />
      </Box>
    </Box>
  );
};

const DetailsContent = ({
  activeTab,
  deltaCashDetails,
  tradingDetails,
  investDetails,
}: DetailsContentProps) => {
  switch (activeTab) {
    case WALLET_TABS.TRADING:
      return tradingDetails;
    case WALLET_TABS.DELTA_CASH:
      return deltaCashDetails;
    case WALLET_TABS.INVEST:
      return investDetails;
    default:
      return null;
  }
};

const WalletDetails = memo(() => {
  const [activeTab, setActiveTab] = useState<WALLET_TABS>(WALLET_TABS.TRADING);

  return (
    <Box className={styles.walletMenuDetails} data-testid="wallet-menu-details">
      <WalletMenuTabs
        activeTab={activeTab}
        tabList={WALLET_TAB_LIST}
        onTabChange={setActiveTab}
      />
      <DetailsContent
        activeTab={activeTab}
        tradingCreditsDetails={null}
        deltaCashDetails={<DeltaCashDetails />}
        investDetails={null}
        tradingDetails={<TradingDetails />}
      />
    </Box>
  );
});

WalletDetails.displayName = 'WalletDetails';
TradingDetails.displayName = 'TradingDetails';
TotalTradingEquity.displayName = 'TotalTradingEquity';
WalletMenuTabs.displayName = 'WalletMenuTabs';
WalletDetailsFAQ.displayName = 'WalletDetailsFAQ';
DetailsContent.displayName = 'DetailsContent';

export { WalletDetails };
